const API_BASE = '/api';

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for session
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'collaborator';
  tier: 'pro' | 'standard';
  avatar_color: string;
  created_at: string;
  last_seen_at: string | null;
}

export interface Tag {
  id: string;
  board_id: string;
  name: string;
  color: string;
}

export interface Card {
  id: string;
  column_id: string;
  title: string;
  description: string | null;
  r2_key: string;
  position: number;
  color_label: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  word_count: number;
  tags: Tag[];
  comment_count: number;
}

export interface Column {
  id: string;
  board_id: string;
  name: string;
  color: string;
  position: number;
  cards: Card[];
}

export interface Board {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  created_at: string;
  columns: Column[];
}

export interface Comment {
  id: string;
  card_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string | null;
  user_name: string | null;
  user_avatar_color: string;
}

export interface Activity {
  id: string;
  board_id: string;
  user_id: string;
  action: string;
  card_id: string | null;
  details: string | null;
  created_at: string;
  user_name: string | null;
  user_avatar_color: string;
  card_title: string | null;
}

export interface Invite {
  id: string;
  email: string;
  token: string;
  tier: 'pro' | 'standard';
  invited_by: string;
  invited_by_name?: string;
  expires_at: string;
  accepted_at: string | null;
  inviteLink?: string;
}

export interface Attachment {
  id: string;
  card_id: string;
  filename: string;
  file_type: 'pdf' | 'docx' | 'doc' | 'xlsx' | 'xls' | 'csv';
  file_size: number;
  r2_key: string;
  uploaded_by: string;
  created_at: string;
}

export interface SessionInfo {
  authenticated: boolean;
  user: User | null;
}

// API functions
export const api = {
  // Auth
  checkSession: () => request<SessionInfo>('/auth/session'),
  login: (email: string, inviteToken?: string) => request<{ success: boolean; message: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, invite_token: inviteToken })
  }),
  logout: () => request<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  // Boards
  listBoards: () => request<(Board & { is_owner: boolean })[]>('/board/list'),
  getBoard: (id?: string) => id
    ? request<Board>(`/board/${id}`)
    : request<Board>('/board'),
  createBoard: (name: string) => request<Board>('/board', { method: 'POST', body: JSON.stringify({ name }) }),
  updateBoard: (id: string, data: { name?: string; description?: string | null }) => request<Board>(`/board/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBoard: (id: string) => request<{ success: boolean }>(`/board/${id}`, { method: 'DELETE' }),
  getActivity: (boardId: string, limit = 50, offset = 0) =>
    request<Activity[]>(`/board/${boardId}/activity?limit=${limit}&offset=${offset}`),

  // Columns
  getColumns: (boardId: string) => request<Column[]>(`/columns?board_id=${encodeURIComponent(boardId)}`),
  createColumn: (data: { board_id: string; name: string; color: string; position?: number }) =>
    request<Column>('/columns', { method: 'POST', body: JSON.stringify(data) }),
  updateColumn: (id: string, data: { name?: string; color?: string; position?: number }) =>
    request<Column>(`/columns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteColumn: (id: string, destinationColumnId?: string) =>
    request<{ success: boolean }>(`/columns/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(destinationColumnId ? { destination_column_id: destinationColumnId } : {}),
    }),

  // Cards
  getCards: (filters?: { board_id?: string; column_id?: string; tag_id?: string; q?: string }) => {
    const params = new URLSearchParams();
    if (filters?.board_id) params.set('board_id', filters.board_id);
    if (filters?.column_id) params.set('column_id', filters.column_id);
    if (filters?.tag_id) params.set('tag_id', filters.tag_id);
    if (filters?.q) params.set('q', filters.q);
    const query = params.toString();
    return request<Card[]>(`/cards${query ? `?${query}` : ''}`);
  },
  createCard: (data: { title: string; description?: string; column_id: string; content: string; color_label?: string }) =>
    request<Card>('/cards', { method: 'POST', body: JSON.stringify(data) }),
  getCard: (id: string) => request<Card>(`/cards/${id}`),
  getCardContent: (id: string) => request<{ content: string }>(`/cards/${id}/content`),
  updateCard: (id: string, data: { title?: string; description?: string; color_label?: string | null }) =>
    request<Card>(`/cards/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  updateCardContent: (id: string, content: string) =>
    request<{ success: boolean }>(`/cards/${id}/content`, { method: 'PATCH', body: JSON.stringify({ content }) }),
  moveCard: (id: string, data: { column_id: string; position: number }) =>
    request<Card>(`/cards/${id}/move`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCard: (id: string) => request<{ success: boolean }>(`/cards/${id}`, { method: 'DELETE' }),
  addTagToCard: (cardId: string, tagId: string) =>
    request<{ success: boolean }>(`/cards/${cardId}/tags`, { method: 'POST', body: JSON.stringify({ tag_id: tagId }) }),
  removeTagFromCard: (cardId: string, tagId: string) =>
    request<{ success: boolean }>(`/cards/${cardId}/tags/${tagId}`, { method: 'DELETE' }),

  // Tags
  getTags: (boardId: string) => request<Tag[]>(`/tags?board_id=${encodeURIComponent(boardId)}`),
  createTag: (data: { board_id: string; name: string; color: string }) =>
    request<Tag>('/tags', { method: 'POST', body: JSON.stringify(data) }),
  updateTag: (id: string, data: { name?: string; color?: string }) =>
    request<Tag>(`/tags/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTag: (id: string) => request<{ success: boolean }>(`/tags/${id}`, { method: 'DELETE' }),

  // Comments
  getComments: (cardId: string) => request<Comment[]>(`/cards/${cardId}/comments`),
  addComment: (cardId: string, content: string) =>
    request<Comment>(`/cards/${cardId}/comments`, { method: 'POST', body: JSON.stringify({ content }) }),
  updateComment: (id: string, content: string) =>
    request<Comment>(`/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ content }) }),
  deleteComment: (id: string) => request<{ success: boolean }>(`/comments/${id}`, { method: 'DELETE' }),

  // Users
  getCurrentUser: () => request<User & { workspace_collaborators: number; max_workspace_collaborators: number }>('/users/me'),
  getWorkspaceCollaborators: () => request<User[]>('/users'),
  removeUser: (id: string) => request<{ success: boolean }>(`/users/${id}`, { method: 'DELETE' }),

  // Workspace sharing (max 2 collaborators)
  inviteToWorkspace: (email: string) =>
    request<{ success: boolean; message: string; collaborator?: { id: string; email: string; name: string } }>('/users/workspace/invite', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),
  removeFromWorkspace: (userId: string) =>
    request<{ success: boolean }>(`/users/workspace/${userId}`, { method: 'DELETE' }),

  // System Invites (Pro users only - invite new users to the platform)
  createInvite: (email: string, tier: 'pro' | 'standard' = 'standard') =>
    request<{ success: boolean; invite: Invite }>('/users/invite', { method: 'POST', body: JSON.stringify({ email, tier }) }),
  getInvites: () => request<Invite[]>('/users/invites'),
  revokeInvite: (id: string) => request<{ success: boolean }>(`/users/invites/${id}`, { method: 'DELETE' }),

  // Attachments
  getAttachments: (cardId: string) => request<Attachment[]>(`/cards/${cardId}/attachments`),
  uploadAttachment: async (cardId: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE}/cards/${cardId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }
    return response.json();
  },
  getAttachmentUrl: (id: string, inline = false) => `${API_BASE}/attachments/${id}${inline ? '?inline=true' : ''}`,
  getAttachmentContent: (id: string) => request<{ content: string; filename: string; file_type: string }>(`/attachments/${id}/content`),
  deleteAttachment: (id: string) => request<{ success: boolean }>(`/attachments/${id}`, { method: 'DELETE' }),
};
